import { useState, useRef } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { X, Wrench, Truck, Camera, Trash2, Plus } from "lucide-react"
import { SpotGrid } from "@/components/SpotGrid"
import { PhotoGallery } from "@/components/PhotoGallery"
import { useAdminAreal } from "@/hooks/useAdminAreal"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"
import { inputClass, labelClass } from "@/lib/styleClasses"
import type { SpotOccupancyResponse, SpotEntry, OccupiedSpot, SpotPhoto } from "@fohet/caravan-booker-shared/types"

// ── helpers ───────────────────────────────────────────────────────────────────

const toIsoDate = (d: Date): string => d.toISOString().slice(0, 10)

// ── sub-components ────────────────────────────────────────────────────────────

interface SpotDetailPanelProps {
    spot: SpotEntry
    arealId: string
    onClose: () => void
}

const SpotDetailPanel = ({ spot, arealId, onClose }: SpotDetailPanelProps) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const toggleMutation = useMutation({
        mutationFn: (maintenance: boolean) =>
            api.post(`/areals/${arealId}/spots/${spot.number}/maintenance`, { maintenance }),
        onSuccess: (_data, maintenance) => {
            toast.success(
                maintenance
                    ? t("admin.spotMap.toastMaintenanceOn")
                    : t("admin.spotMap.toastMaintenanceOff"),
            )
            void queryClient.invalidateQueries({ queryKey: ["admin", "spotMap"] })
            void queryClient.invalidateQueries({ queryKey: ["admin", "spotPhotos", arealId] })
            onClose()
        },
        onError: () => {
            toast.error(t("admin.spotMap.toastFailed"))
        },
    })

    const { data: photos = [] } = useQuery({
        queryKey: ["admin", "spotPhotos", arealId, spot.number],
        queryFn: () => api.get<SpotPhoto[]>(`/areals/${arealId}/spots/${spot.number}/photos`),
    })

    const uploadMutation = useMutation({
        mutationFn: (file: File) => {
            const formData = new FormData()
            formData.append("file", file)
            return api.upload<SpotPhoto>(`/areals/${arealId}/spots/${spot.number}/photos`, formData)
        },
        onSuccess: () => {
            toast.success(t("admin.spotMap.toastPhotoUploaded"))
            void queryClient.invalidateQueries({ queryKey: ["admin", "spotPhotos", arealId, spot.number] })
            void queryClient.invalidateQueries({ queryKey: ["admin", "spotPhotos", arealId] })
        },
        onError: () => toast.error(t("admin.spotMap.toastPhotoFailed")),
    })

    const deleteMutation = useMutation({
        mutationFn: (photoId: string) =>
            api.delete(`/areals/${arealId}/spots/${spot.number}/photos/${photoId}`),
        onSuccess: () => {
            toast.success(t("admin.spotMap.toastPhotoDeleted"))
            void queryClient.invalidateQueries({ queryKey: ["admin", "spotPhotos", arealId, spot.number] })
            void queryClient.invalidateQueries({ queryKey: ["admin", "spotPhotos", arealId] })
        },
        onError: () => toast.error(t("admin.spotMap.toastPhotoDeleteFailed")),
    })

    return (
        <div className="bg-white rounded-[28px] border border-slate-100 shadow-2xl p-6 min-w-[260px]">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <h3 className="text-base font-black text-slate-900">
                    {t("admin.spotMap.spotDetail", { number: spot.number })}
                </h3>
                <button
                    onClick={onClose}
                    className="text-slate-400 hover:text-slate-600 transition-colors ml-3"
                    aria-label={t("admin.detail.close")}
                >
                    <X className="size-4" />
                </button>
            </div>

            {spot.status === "available" && (
                <div className="space-y-3">
                    <p className="text-sm font-bold text-slate-500">{t("admin.spotMap.noReservation")}</p>
                    <button
                        onClick={() => toggleMutation.mutate(true)}
                        disabled={toggleMutation.isPending}
                        className={cn(
                            "w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all duration-200 active:scale-95",
                            "bg-slate-100 text-slate-700 hover:bg-slate-200",
                        )}
                    >
                        <Wrench className="size-4" />
                        {t("admin.spotMap.markMaintenance")}
                    </button>
                </div>
            )}

            {spot.status === "occupied" && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-red-50 text-red-600 text-xs font-black uppercase tracking-widest">
                            <Truck className="size-3" />
                            {t("admin.spotMap.occupied")}
                        </span>
                    </div>
                    {[
                        { label: t("admin.spotMap.licensePlate"), value: (spot as OccupiedSpot).licensePlate },
                        { label: t("admin.spotMap.guest"), value: (spot as OccupiedSpot).guestName },
                        {
                            label: t("admin.spotMap.stay"),
                            value: `${(spot as OccupiedSpot).arrivalDate} → ${(spot as OccupiedSpot).departureDate}`,
                        },
                    ].map(({ label, value }) => (
                        <div key={label}>
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">
                                {label}
                            </p>
                            <p className="text-sm font-bold text-slate-800">{value}</p>
                        </div>
                    ))}
                </div>
            )}

            {spot.status === "maintenance" && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-slate-100 text-slate-500 text-xs font-black uppercase tracking-widest">
                            <Wrench className="size-3" />
                            {t("admin.spotMap.maintenance")}
                        </span>
                    </div>
                    <button
                        onClick={() => toggleMutation.mutate(false)}
                        disabled={toggleMutation.isPending}
                        className={cn(
                            "w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all duration-200 active:scale-95",
                            "bg-brand text-white hover:bg-brand-dark shadow-md",
                        )}
                    >
                        <Wrench className="size-4" />
                        {t("admin.spotMap.removeMaintenance")}
                    </button>
                </div>
            )}

            {/* Photo management — available for all spot states */}
            <div className="border-t border-slate-100 mt-4 pt-4 space-y-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {t("admin.spotMap.spotPhotos")}
                </p>

                {photos.length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                        {photos.map((photo) => (
                            <div key={photo.id} className="relative group">
                                <img
                                    src={photo.url}
                                    alt={photo.altText ?? ""}
                                    className="w-full h-16 object-cover rounded-xl"
                                />
                                <button
                                    type="button"
                                    onClick={() => deleteMutation.mutate(photo.id)}
                                    disabled={deleteMutation.isPending}
                                    className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                    aria-label={t("admin.spotMap.deletePhoto")}
                                >
                                    <Trash2 className="size-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.currentTarget.files?.[0]
                        if (file) uploadMutation.mutate(file)
                        e.currentTarget.value = ""
                    }}
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadMutation.isPending}
                    className={cn(
                        "w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-black transition-all duration-200 active:scale-95",
                        "bg-slate-100 text-slate-700 hover:bg-slate-200",
                    )}
                >
                    {uploadMutation.isPending
                        ? <><Camera className="size-3" /> {t("admin.spotMap.uploading")}</>
                        : <><Plus className="size-3" /> {t("admin.spotMap.uploadPhoto")}</>
                    }
                </button>
            </div>
        </div>
    )
}

// ── page ──────────────────────────────────────────────────────────────────────

export const AdminSpotMapPage = () => {
    const { t } = useTranslation()
    const { data: areal } = useAdminAreal()
    const [date, setDate] = useState<string>(toIsoDate(new Date()))
    const [selectedSpot, setSelectedSpot] = useState<number | null>(null)

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "spotMap", areal?.id, date],
        queryFn: () =>
            api.get<SpotOccupancyResponse>(`/areals/${areal!.id}/spots?date=${date}`),
        enabled: !!areal?.id,
    })

    const { data: spotPhotosMap } = useQuery({
        queryKey: ["admin", "spotPhotos", areal?.id],
        queryFn: () => api.get<Record<string, Array<{ id: string; url: string; altText: string | null; order: number }>>>(`/areals/${areal!.id}/spot-photos`),
        enabled: !!areal?.id,
    })

    const spotsWithPhotos = new Set(
        Object.keys(spotPhotosMap ?? {})
            .filter((k) => (spotPhotosMap?.[k]?.length ?? 0) > 0)
            .map(Number),
    )

    const spotList = data?.spots ?? []

    const occupiedNumbers = spotList
        .filter((s): s is Extract<SpotEntry, { status: "occupied" }> => s.status === "occupied")
        .map((s) => s.number)

    const maintenanceNumbers = spotList
        .filter((s): s is Extract<SpotEntry, { status: "maintenance" }> => s.status === "maintenance")
        .map((s) => s.number)

    const availableCount = spotList.filter((s) => s.status === "available").length
    const occupiedCount = occupiedNumbers.length
    const maintenanceCount = maintenanceNumbers.length

    const selectedSpotEntry = selectedSpot !== null
        ? spotList.find((s) => s.number === selectedSpot) ?? null
        : null

    const handleSelect = (n: number) => {
        setSelectedSpot((prev) => (prev === n ? null : n))
    }

    if (!areal) {
        return (
            <div className="text-slate-400 font-bold py-8 text-center">
                {t("admin.pricing.noAreal")}
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-slate-900">
                        {t("admin.spotMap.title")}
                    </h1>
                    <p className="text-slate-500 font-bold mt-1">{t("admin.spotMap.subtitle")}</p>
                </div>
                {/* Date picker */}
                <div>
                    <label className={labelClass}>
                        {t("admin.spotMap.date")}
                    </label>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => {
                            setDate(e.target.value)
                            setSelectedSpot(null)
                        }}
                        className={inputClass}
                    />
                </div>
            </div>

            {/* Stats bar */}
            <div className="flex items-center gap-3 flex-wrap">
                {[
                    {
                        label: t("admin.spotMap.available"),
                        count: availableCount,
                        cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
                    },
                    {
                        label: t("admin.spotMap.occupied"),
                        count: occupiedCount,
                        cls: "bg-red-50 text-red-600 border-red-200",
                    },
                    {
                        label: t("admin.spotMap.maintenance"),
                        count: maintenanceCount,
                        cls: "bg-slate-100 text-slate-500 border-slate-200",
                    },
                ].map(({ label, count, cls }) => (
                    <span
                        key={label}
                        className={cn(
                            "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-black uppercase tracking-widest",
                            cls,
                        )}
                    >
                        <span className="text-base font-black leading-none">{count}</span>
                        {label}
                    </span>
                ))}
            </div>

            {/* Grid + detail panel side by side on wider screens */}
            <div className="flex flex-col lg:flex-row gap-6 items-start">
                {/* Spot grid */}
                <div className="flex-1 min-w-0">
                    {isLoading ? (
                        <div className="text-slate-400 font-bold py-12 text-center">
                            {t("common.loading")}
                        </div>
                    ) : (
                        <SpotGrid
                            total={areal.spotCount}
                            occupied={occupiedNumbers}
                            maintenance={maintenanceNumbers}
                            selected={selectedSpot}
                            onSelect={handleSelect}
                            allowMaintenanceClick
                            hasPhotos={spotsWithPhotos}
                        />
                    )}
                </div>

                {/* Detail panel */}
                {selectedSpotEntry && areal.id && (
                    <div className="lg:w-72 w-full shrink-0">
                        <SpotDetailPanel
                            spot={selectedSpotEntry}
                            arealId={areal.id}
                            onClose={() => setSelectedSpot(null)}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
